import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FiSave,
  FiEye,
  FiArrowLeft,
  FiLoader,
  FiGlobe,
  FiBold,
  FiItalic,
  FiLink,
  FiList,
  FiImage,
  FiCode,
  FiEdit3,
  FiCheck,
  FiAlertCircle,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MediaPicker } from "@/components/common";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  featuredImage: string | null;
  status: "draft" | "published" | "archived";
  metaTitle: string | null;
  metaDescription: string | null;
  publishedAt: string | null;
  createdAt: string;
};

type FormData = {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  status: "draft" | "published" | "archived";
  metaTitle: string;
  metaDescription: string;
};

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// Validation hint component
function ValidationHint({ valid, message, showIcon = true }: { valid: boolean; message: string; showIcon?: boolean }) {
  return (
    <p className={`text-xs mt-1 flex items-center gap-1 ${valid ? "text-muted-foreground" : "text-red-500"}`}>
      {showIcon && (
        valid ? <FiCheck className="w-3 h-3 text-green-500" /> : <FiAlertCircle className="w-3 h-3" />
      )}
      {message}
    </p>
  );
}

// Simple markdown to HTML converter for preview
function markdownToHtml(markdown: string): string {
  let html = markdown
    // Escape HTML
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-6 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-8 mb-3">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline">$1</a>')
    // Images
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="rounded-lg my-4 max-w-full" />')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-muted px-1.5 py-0.5 rounded text-sm">$1</code>')
    // Code blocks
    .replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-4 rounded-lg overflow-x-auto my-4"><code>$1</code></pre>')
    // Unordered lists
    .replace(/^\s*[-*]\s+(.*)$/gim, '<li class="ml-4">$1</li>')
    // Ordered lists
    .replace(/^\s*\d+\.\s+(.*)$/gim, '<li class="ml-4 list-decimal">$1</li>')
    // Blockquotes
    .replace(/^>\s+(.*)$/gim, '<blockquote class="border-l-4 border-primary pl-4 italic my-4">$1</blockquote>')
    // Horizontal rule
    .replace(/^---$/gim, '<hr class="my-8 border-border" />')
    // Paragraphs (double newlines)
    .replace(/\n\n/g, '</p><p class="mb-4">')
    // Line breaks
    .replace(/\n/g, "<br />");

  // Wrap in paragraph
  html = `<p class="mb-4">${html}</p>`;

  // Clean up empty paragraphs
  html = html.replace(/<p class="mb-4"><\/p>/g, "");

  return html;
}

export function BlogEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [post, setPost] = useState<Post | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featuredImage: "",
    status: "draft",
    metaTitle: "",
    metaDescription: "",
  });

  const [autoSlug, setAutoSlug] = useState(true);

  // Validation rules
  const validation = useMemo(() => {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    const urlRegex = /^https?:\/\/.+/;

    return {
      title: {
        valid: formData.title.length >= 1 && formData.title.length <= 200,
        message: formData.title.length === 0
          ? "Required"
          : formData.title.length > 200
            ? `${formData.title.length}/200 - too long`
            : `${formData.title.length}/200`,
      },
      slug: {
        valid: formData.slug.length >= 1 && formData.slug.length <= 200 && slugRegex.test(formData.slug),
        message: formData.slug.length === 0
          ? "Required"
          : !slugRegex.test(formData.slug)
            ? "Lowercase letters, numbers, and hyphens only"
            : `${formData.slug.length}/200`,
      },
      content: {
        valid: formData.content.length >= 1,
        message: formData.content.length === 0 ? "Required" : `${formData.content.length} characters`,
      },
      excerpt: {
        valid: formData.excerpt.length <= 500,
        message: `${formData.excerpt.length}/500`,
      },
      featuredImage: {
        valid: formData.featuredImage === "" || urlRegex.test(formData.featuredImage),
        message: formData.featuredImage && !urlRegex.test(formData.featuredImage)
          ? "Must be a valid URL (https://...)"
          : "Optional",
      },
      metaTitle: {
        valid: formData.metaTitle.length <= 70,
        message: `${formData.metaTitle.length}/70`,
      },
      metaDescription: {
        valid: formData.metaDescription.length <= 160,
        message: `${formData.metaDescription.length}/160`,
      },
    };
  }, [formData]);

  const isFormValid = validation.title.valid && validation.slug.valid && validation.content.valid &&
    validation.excerpt.valid && validation.featuredImage.valid && validation.metaTitle.valid && validation.metaDescription.valid;

  // Insert markdown formatting at cursor position
  const insertFormat = (before: string, after: string = "", placeholder: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newContent =
      formData.content.substring(0, start) +
      before +
      textToInsert +
      after +
      formData.content.substring(end);

    setFormData({ ...formData, content: newContent });

    // Set cursor position after update
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(
        selectedText ? newCursorPos + after.length : start + before.length,
        selectedText ? newCursorPos + after.length : start + before.length + placeholder.length
      );
    }, 0);
  };

  const formatActions = [
    { icon: FiBold, label: "Bold", action: () => insertFormat("**", "**", "bold text") },
    { icon: FiItalic, label: "Italic", action: () => insertFormat("*", "*", "italic text") },
    { icon: FiLink, label: "Link", action: () => insertFormat("[", "](url)", "link text") },
    { icon: FiImage, label: "Image", action: () => insertFormat("![alt text](", ")", "image-url") },
    { icon: FiList, label: "List", action: () => insertFormat("\n- ", "", "list item") },
    { icon: FiCode, label: "Code", action: () => insertFormat("`", "`", "code") },
    { label: "H1", action: () => insertFormat("# ", "", "Heading 1") },
    { label: "H2", action: () => insertFormat("## ", "", "Heading 2") },
    { label: "H3", action: () => insertFormat("### ", "", "Heading 3") },
    { label: "Quote", action: () => insertFormat("> ", "", "quote") },
  ];

  // Fetch post if editing
  useEffect(() => {
    if (isEditing && id) {
      fetch(`/api/admin/blog/posts/${id}`, { credentials: "include" })
        .then((res) => res.json())
        .then((data) => {
          setPost(data as Post);
          setFormData({
            title: (data as Post).title || "",
            slug: (data as Post).slug || "",
            excerpt: (data as Post).excerpt || "",
            content: (data as Post).content || "",
            featuredImage: (data as Post).featuredImage || "",
            status: (data as Post).status || "draft",
            metaTitle: (data as Post).metaTitle || "",
            metaDescription: (data as Post).metaDescription || "",
          });
          setAutoSlug(false);
        })
        .catch((err) => {
          console.error("Failed to fetch post:", err);
          toast.error("Failed to load post");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, isEditing]);

  // Auto-generate slug from title
  useEffect(() => {
    if (autoSlug && formData.title) {
      setFormData((prev) => ({
        ...prev,
        slug: generateSlug(formData.title),
      }));
    }
  }, [formData.title, autoSlug]);

  const handleSubmit = async (status?: "draft" | "published") => {
    const submitStatus = status || formData.status;

    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        title: formData.title,
        slug: formData.slug,
        content: formData.content,
        status: submitStatus,
        // Send null for empty optional fields (Zod expects URL or null, not empty string)
        featuredImage: formData.featuredImage.trim() || null,
        excerpt: formData.excerpt.trim() || null,
        metaTitle: formData.metaTitle.trim() || null,
        metaDescription: formData.metaDescription.trim() || null,
      };

      const url = isEditing
        ? `/api/admin/blog/posts/${id}`
        : "/api/admin/blog/posts";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = (await response.json()) as { error?: string };

      if (!response.ok) {
        throw new Error(data.error || "Failed to save post");
      }

      toast.success(
        isEditing
          ? "Post updated successfully"
          : submitStatus === "published"
            ? "Post published!"
            : "Draft saved"
      );

      navigate("/admin/blog");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save post"
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FiLoader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/blog")}
          >
            <FiArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-serif text-2xl font-medium text-slate-900">
              {isEditing ? "Edit Post" : "New Post"}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {formData.status === "published" && formData.slug && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/blog/${formData.slug}`, "_blank")}
            >
              <FiGlobe className="w-4 h-4 mr-2" />
              View Live
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSaving || !isFormValid}
          >
            {isSaving ? (
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FiSave className="w-4 h-4 mr-2" />
            )}
            Save Draft
          </Button>
          <Button
            onClick={() => handleSubmit("published")}
            disabled={isSaving || !isFormValid}
            className="btn-pill"
          >
            {isSaving ? (
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FiGlobe className="w-4 h-4 mr-2" />
            )}
            {formData.status === "published" ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      {/* Validation Summary */}
      {!isFormValid && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Please fix the following before saving:</p>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                {!validation.title.valid && <li>Title: {validation.title.message}</li>}
                {!validation.slug.valid && <li>Slug: {validation.slug.message}</li>}
                {!validation.content.valid && <li>Content: {validation.content.message}</li>}
                {!validation.excerpt.valid && <li>Excerpt: {validation.excerpt.message}</li>}
                {!validation.featuredImage.valid && <li>Featured Image: {validation.featuredImage.message}</li>}
                {!validation.metaTitle.valid && <li>Meta Title: {validation.metaTitle.message}</li>}
                {!validation.metaDescription.valid && <li>Meta Description: {validation.metaDescription.message}</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="title">
                  Title *
                </label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Enter post title..."
                  className={`mt-1 ${!validation.title.valid && formData.title ? "border-red-500" : ""}`}
                />
                <ValidationHint valid={validation.title.valid} message={validation.title.message} />
              </div>

              <div>
                <label className="text-sm font-medium" htmlFor="slug">
                  Slug *
                </label>
                <div className="flex gap-2 mt-1">
                  <div className="flex-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      /blog/
                    </span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => {
                        setAutoSlug(false);
                        setFormData({ ...formData, slug: e.target.value });
                      }}
                      placeholder="post-slug"
                      className={`pl-14 ${!validation.slug.valid && formData.slug ? "border-red-500" : ""}`}
                    />
                  </div>
                </div>
                <ValidationHint valid={validation.slug.valid} message={validation.slug.message} />
              </div>

              <div>
                <label className="text-sm font-medium" htmlFor="excerpt">
                  Excerpt
                </label>
                <textarea
                  id="excerpt"
                  value={formData.excerpt}
                  onChange={(e) =>
                    setFormData({ ...formData, excerpt: e.target.value })
                  }
                  placeholder="Brief summary of the post..."
                  rows={2}
                  className={`mt-1 w-full px-3 py-2 border border-input rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring ${!validation.excerpt.valid ? "border-red-500" : ""}`}
                />
                <ValidationHint valid={validation.excerpt.valid} message={validation.excerpt.message} showIcon={false} />
              </div>

              <div>
                {/* Label with Preview Toggle */}
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium" htmlFor="content">
                    Content *
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {showPreview ? (
                      <>
                        <FiEdit3 className="w-4 h-4 mr-1" />
                        Edit
                      </>
                    ) : (
                      <>
                        <FiEye className="w-4 h-4 mr-1" />
                        Preview
                      </>
                    )}
                  </Button>
                </div>

                {/* Formatting Toolbar */}
                {!showPreview && (
                  <div className="flex flex-wrap gap-1 p-2 bg-muted rounded-t-md border border-b-0 border-input">
                    {formatActions.map((action, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={action.action}
                        className="px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-background rounded transition-colors"
                        title={action.label}
                      >
                        {action.icon ? (
                          <action.icon className="w-4 h-4" />
                        ) : (
                          action.label
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Editor or Preview */}
                {showPreview ? (
                  <div className="border border-input rounded-md p-4 min-h-[400px] bg-white prose prose-sm max-w-none">
                    {formData.content ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: markdownToHtml(formData.content),
                        }}
                      />
                    ) : (
                      <p className="text-muted-foreground italic">
                        Nothing to preview yet...
                      </p>
                    )}
                  </div>
                ) : (
                  <textarea
                    ref={textareaRef}
                    id="content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    placeholder="Write your post content here..."
                    rows={20}
                    className="w-full px-3 py-2 border border-input rounded-b-md text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                )}

                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted-foreground">
                    Supports Markdown formatting. Use the toolbar above or type markdown directly.
                  </p>
                  <ValidationHint valid={validation.content.valid} message={validation.content.message} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                {(["draft", "published", "archived"] as const).map((status) => (
                  <Button
                    key={status}
                    variant={formData.status === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFormData({ ...formData, status })}
                    className="capitalize"
                  >
                    {status}
                  </Button>
                ))}
              </div>
              {post?.publishedAt && (
                <p className="text-xs text-muted-foreground mt-3">
                  Published {new Date(post.publishedAt).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Featured Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Featured Image</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={formData.featuredImage}
                  onChange={(e) =>
                    setFormData({ ...formData, featuredImage: e.target.value })
                  }
                  placeholder="https://example.com/image.jpg"
                  className={`flex-1 ${!validation.featuredImage.valid ? "border-red-500" : ""}`}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => setMediaPickerOpen(true)} className="h-9 gap-1.5 shrink-0">
                  <FiImage className="w-3.5 h-3.5" />
                  Browse
                </Button>
              </div>
              <ValidationHint valid={validation.featuredImage.valid} message={validation.featuredImage.message} showIcon={formData.featuredImage.length > 0} />
              {formData.featuredImage && validation.featuredImage.valid && (
                <div className="mt-3 rounded-lg overflow-hidden border bg-slate-50 p-2">
                  <img
                    src={formData.featuredImage}
                    alt="Cover preview"
                    className="max-h-32 mx-auto rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <MediaPicker
                open={mediaPickerOpen}
                onOpenChange={setMediaPickerOpen}
                onSelect={(url) => setFormData({ ...formData, featuredImage: url })}
                folder="blog"
                title="Choose Cover Image"
              />
            </CardContent>
          </Card>

          {/* SEO */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="metaTitle">
                  Meta Title
                </label>
                <Input
                  id="metaTitle"
                  value={formData.metaTitle}
                  onChange={(e) =>
                    setFormData({ ...formData, metaTitle: e.target.value })
                  }
                  placeholder={formData.title || "Post title"}
                  className={`mt-1 ${!validation.metaTitle.valid ? "border-red-500" : ""}`}
                />
                <ValidationHint valid={validation.metaTitle.valid} message={validation.metaTitle.message} showIcon={false} />
              </div>
              <div>
                <label
                  className="text-sm font-medium"
                  htmlFor="metaDescription"
                >
                  Meta Description
                </label>
                <textarea
                  id="metaDescription"
                  value={formData.metaDescription}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      metaDescription: e.target.value,
                    })
                  }
                  placeholder={formData.excerpt || "Post excerpt"}
                  rows={3}
                  className={`mt-1 w-full px-3 py-2 border border-input rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring ${!validation.metaDescription.valid ? "border-red-500" : ""}`}
                />
                <ValidationHint valid={validation.metaDescription.valid} message={validation.metaDescription.message} showIcon={false} />
              </div>
            </CardContent>
          </Card>

          {/* Markdown Help */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Formatting Help</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground space-y-1.5 font-mono">
                <p><span className="text-foreground">**bold**</span> = <strong>bold</strong></p>
                <p><span className="text-foreground">*italic*</span> = <em>italic</em></p>
                <p><span className="text-foreground"># Heading</span> = H1</p>
                <p><span className="text-foreground">## Heading</span> = H2</p>
                <p><span className="text-foreground">[text](url)</span> = link</p>
                <p><span className="text-foreground">- item</span> = bullet</p>
                <p><span className="text-foreground">`code`</span> = inline code</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
